import { useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles, tokens, Text, Badge, Button, Spinner, Divider, Checkbox,
  Dropdown, Option, Field,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import type { PromptDetail, GoldenDatasetSummary } from '../api/types';
import { listDatasets } from '../api/datasets';
import { useChatModels } from '../hooks/useChatModels';
import { providerBadgeColor, providerLabel } from '../lib/chatModelId';
import {
  runPromptAbTest, preflightPromptAbTest, listPromptAbTests, getPromptAbTest,
  promoteWinner,
} from '../api/promptAbTest';
import type {
  PromptAbTestResponse, PromptAbTestListItem, PromptAbTestPreflightResponse,
  PromptAbTestPerModelDto, PromptAbVerdict,
} from '../api/promptAbTest';
import { PROMPT_AB_VERDICT } from '../api/promptAbTest';
import { getMatrixRun } from '../api/matrix';
import type { MatrixRunResponse, MatrixCellDto } from '../api/matrix';

// ============================================================================
// A/B Test panel — mounted inside PromptOptimization's Compare Versions tab.
// Scoped to one prompt (prop). Handles picker → preflight → run → verdict +
// per-model breakdown → per-question drill-down + history.
//
// Remount pattern: parent should pass key={prompt.id} so internal state
// (dataset, models, result, history) resets cleanly on prompt switch — no
// useEffect reset chain needed.
// ============================================================================

interface Props {
  prompt: PromptDetail;
  /** Called after a successful promote-winner. Parent should refetch the
   *  prompt detail so the production badge + version list update in place. */
  onPromoted?: (newProductionVersionId: string) => void;
}

const LOADING_MESSAGES = [
  'Fanning out matrix A...',
  'Fanning out matrix B...',
  'Scoring cells against expected keywords...',
  'Computing per-model verdicts...',
];

// Verdict → badge color. InsufficientData is informative, not warning —
// it's a data-volume signal, not a quality problem.
function verdictColor(v: PromptAbVerdict): 'success' | 'brand' | 'informative' | 'warning' {
  switch (v) {
    case PROMPT_AB_VERDICT.AWins: return 'brand';
    case PROMPT_AB_VERDICT.BWins: return 'success';
    case PROMPT_AB_VERDICT.Tie: return 'warning';
    case PROMPT_AB_VERDICT.InsufficientData: return 'informative';
  }
}

function verdictLabel(v: PromptAbVerdict): string {
  switch (v) {
    case PROMPT_AB_VERDICT.AWins: return 'A wins';
    case PROMPT_AB_VERDICT.BWins: return 'B wins';
    case PROMPT_AB_VERDICT.Tie: return 'Tie';
    case PROMPT_AB_VERDICT.InsufficientData: return 'Insufficient data';
  }
}

function pct(v: number) { return `${(v * 100).toFixed(0)}%`; }

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' },
  pickerRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  configBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
  },
  modelGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 10px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '6px',
    background: tokens.colorNeutralBackground1,
    minWidth: '200px',
  },
  modelGroupRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  preflightBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    background: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '13px',
  },
  preflightWarn: {
    background: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  runRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  verdictCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontWeight: 600,
    background: tokens.colorNeutralBackground2,
  },
  td: {
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    verticalAlign: 'middle',
  },
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    cursor: 'pointer',
  },
  historyRowActive: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  drillHalf: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '10px',
    borderRadius: '6px',
    background: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  drillAnswer: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    background: tokens.colorNeutralBackground3,
    padding: '8px',
    borderRadius: '4px',
    maxHeight: '220px',
    overflow: 'auto',
  },
});

export default function AbTestPanel({ prompt, onPromoted }: Props) {
  const s = useStyles();

  // ---- Version defaults: A = production (or v1), B = latest draft (or highest)
  const [versionAId, setVersionAId] = useState<string>(() => defaultVersionA(prompt));
  const [versionBId, setVersionBId] = useState<string>(() => defaultVersionB(prompt));

  // Datasets filtered to prompt's role
  const [datasets, setDatasets] = useState<GoldenDatasetSummary[]>([]);
  const [datasetId, setDatasetId] = useState<string | null>(null);

  // Models — reuse M4 hook, pre-select all like MatrixLab does
  const { models, byProvider } = useChatModels(true);
  const [selectedChatModelIds, setSelectedChatModelIds] = useState<Set<string>>(new Set());
  const seededRef = useRef(false);
  useEffect(() => {
    if (models.length > 0 && !seededRef.current) {
      setSelectedChatModelIds(new Set(models.map(m => m.chatModelId)));
      seededRef.current = true;
    }
  }, [models]);

  // Preflight + run state
  const [preflight, setPreflight] = useState<PromptAbTestPreflightResponse | null>(null);
  const [preflightErr, setPreflightErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<PromptAbTestResponse | null>(null);

  // History
  const [history, setHistory] = useState<PromptAbTestListItem[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  // Drill (per-question side-by-side)
  const [drill, setDrill] = useState<PromptAbTestPerModelDto | null>(null);
  const [drillCells, setDrillCells] = useState<{ a: MatrixCellDto[]; b: MatrixCellDto[] } | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  // Phase 5 M3 — promote-winner
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteErr, setPromoteErr] = useState<string | null>(null);

  // ---- Load datasets for this role
  useEffect(() => {
    listDatasets(prompt.role)
      .then(setDatasets)
      .catch(e => setErr(`Could not load datasets: ${(e as Error).message}`));
  }, [prompt.role]);

  // ---- Load history for this prompt (initial + after run)
  useEffect(() => {
    listPromptAbTests(prompt.id, 20)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [prompt.id, result?.abTestId]);

  // ---- Auto-swap: if user picks A with a higher versionNumber than B, swap.
  // Runs after both change; guarded to not fight the user mid-selection.
  const swapGuard = useRef(false);
  useEffect(() => {
    if (swapGuard.current) { swapGuard.current = false; return; }
    const vA = prompt.versions.find(v => v.id === versionAId);
    const vB = prompt.versions.find(v => v.id === versionBId);
    if (vA && vB && vA.versionNumber > vB.versionNumber) {
      swapGuard.current = true;
      setVersionAId(versionBId);
      setVersionBId(versionAId);
    }
  }, [versionAId, versionBId, prompt.versions]);

  // ---- Debounced preflight (300ms) — same UX as MatrixLab
  useEffect(() => {
    if (!datasetId || selectedChatModelIds.size === 0 || versionAId === versionBId) {
      setPreflight(null);
      setPreflightErr(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await preflightPromptAbTest(prompt.id, {
          versionAId, versionBId, datasetId,
          chatModelIds: Array.from(selectedChatModelIds),
        });
        if (!cancelled) { setPreflight(res); setPreflightErr(null); }
      } catch (e) {
        if (!cancelled) {
          setPreflight(null);
          const msg = (e as Error).message;
          const m = msg.match(/^API \d+: (.+)$/s);
          setPreflightErr(m ? m[1] : msg);
        }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [prompt.id, versionAId, versionBId, datasetId, selectedChatModelIds]);

  // ---- Loading message rotation
  const loadingRef = useRef(0);
  useEffect(() => {
    if (!busy) return;
    loadingRef.current = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    const id = setInterval(() => {
      loadingRef.current = (loadingRef.current + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[loadingRef.current]);
    }, 3000);
    return () => clearInterval(id);
  }, [busy]);

  const preflightWarn = preflight !== null
    && (preflight.totalEstimatedCostUsd > 1.0 || preflight.totalEstimatedSeconds > 300);

  const canRun = !busy
    && datasetId !== null
    && versionAId !== versionBId
    && selectedChatModelIds.size > 0;

  function toggleModel(id: string) {
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleProviderGroup(providerId: string) {
    const ids = (byProvider.get(providerId) ?? []).map(m => m.chatModelId);
    const allOn = ids.every(id => selectedChatModelIds.has(id));
    setSelectedChatModelIds(prev => {
      const next = new Set(prev);
      for (const id of ids) { if (allOn) next.delete(id); else next.add(id); }
      return next;
    });
  }

  async function handleRun() {
    if (!datasetId) return;
    setErr(null);
    setBusy(true);
    setResult(null);
    setSelectedHistoryId(null);
    try {
      const res = await runPromptAbTest(prompt.id, {
        versionAId, versionBId, datasetId,
        chatModelIds: Array.from(selectedChatModelIds),
      });
      setResult(res);
      setSelectedHistoryId(res.abTestId);
    } catch (e) {
      const msg = (e as Error).message;
      const m = msg.match(/^API \d+: (.+)$/s);
      setErr(m ? m[1] : msg);
    } finally {
      setBusy(false);
    }
  }

  async function loadHistoryItem(abTestId: string) {
    setErr(null);
    setSelectedHistoryId(abTestId);
    try {
      const res = await getPromptAbTest(abTestId);
      setResult(res);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  // ---- Drill: fetch both matrix runs, filter cells to this row's chatModelId
  async function openDrill(row: PromptAbTestPerModelDto) {
    if (!result) return;
    setDrill(row);
    setDrillCells(null);
    setDrillLoading(true);
    try {
      const [runA, runB] = await Promise.all([
        getMatrixRun(result.matrixRunAId),
        getMatrixRun(result.matrixRunBId),
      ]);
      setDrillCells({
        a: filterAndSort(runA, row.chatModelId),
        b: filterAndSort(runB, row.chatModelId),
      });
    } catch (e) {
      setErr(`Drill-down failed: ${(e as Error).message}`);
      setDrill(null);
    } finally {
      setDrillLoading(false);
    }
  }

  // ---- Phase 5 M3 promote-winner gating -----------------------------------
  // Winner is derived from stored verdict + versionA/B ids. Gate follows the
  // backend's PROMOTE_WINNER contract exactly so the UI never lets the user
  // send a request that the server would reject: verdict decisive, winner
  // Approved, winner not already Production. Mismatch is a bug, not UX.
  const winningVersionId =
    result && result.verdict === PROMPT_AB_VERDICT.AWins ? result.versionAId :
    result && result.verdict === PROMPT_AB_VERDICT.BWins ? result.versionBId :
    null;
  const winningVersion = winningVersionId
    ? prompt.versions.find(v => v.id === winningVersionId) ?? null
    : null;
  const winningLabel = winningVersion
    ? (result?.verdict === PROMPT_AB_VERDICT.AWins ? 'A' : 'B') + ` (v${winningVersion.versionNumber})`
    : '';

  const promoteReason: string | null = !result
    ? 'Run an A/B test first.'
    : result.verdict === PROMPT_AB_VERDICT.Tie
      ? 'Tie verdict — no decisive winner to promote.'
      : result.verdict === PROMPT_AB_VERDICT.InsufficientData
        ? 'Insufficient data — need at least 3 samples per side on at least one model.'
        : !winningVersion
          ? 'Winning version not found on this prompt.'
          : winningVersion.status !== 'Approved'
            ? `Version ${winningVersion.versionNumber} must be Approved before it can be promoted.`
            : prompt.productionVersionId === winningVersionId
              ? 'Winning version is already the current production version.'
              : null;
  const canPromote = promoteReason === null && !promoteBusy;

  async function handlePromote() {
    if (!result || !winningVersionId) return;
    setPromoteBusy(true);
    setPromoteErr(null);
    try {
      const res = await promoteWinner(prompt.id, { abTestId: result.abTestId });
      setPromoteOpen(false);
      onPromoted?.(res.toVersionId);
    } catch (e) {
      const msg = (e as Error).message;
      const m = msg.match(/^API \d+: (.+)$/s);
      setPromoteErr(m ? m[1] : msg);
    } finally {
      setPromoteBusy(false);
    }
  }

  // ---- Version dropdown labels: "v3 (prod)" / "v4 (Draft)"
  const versionLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of prompt.versions) {
      const suffix = v.isProduction ? ' (prod)' : v.status === 'Draft' ? ' (draft)' : '';
      m.set(v.id, `v${v.versionNumber}${suffix}`);
    }
    return m;
  }, [prompt.versions]);

  const timelineOrder = useMemo(
    () => [...prompt.versions].sort((a, b) => a.versionNumber - b.versionNumber),
    [prompt.versions],
  );

  return (
    <div className={s.root}>
      {err && <Text size={200} className={s.err}>{err}</Text>}

      {/* ---------------- Version + Dataset pickers ---------------- */}
      <div className={s.pickerRow}>
        <Field label="Version A (baseline)">
          <Dropdown
            value={versionLabel.get(versionAId) ?? ''}
            selectedOptions={[versionAId]}
            onOptionSelect={(_e, d) => d.optionValue && setVersionAId(d.optionValue)}
            disabled={busy}
          >
            {timelineOrder.map(v => (
              <Option key={v.id} value={v.id} text={versionLabel.get(v.id) ?? ''}>
                {versionLabel.get(v.id)}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Version B (candidate)">
          <Dropdown
            value={versionLabel.get(versionBId) ?? ''}
            selectedOptions={[versionBId]}
            onOptionSelect={(_e, d) => d.optionValue && setVersionBId(d.optionValue)}
            disabled={busy}
          >
            {timelineOrder.map(v => (
              <Option key={v.id} value={v.id} text={versionLabel.get(v.id) ?? ''}>
                {versionLabel.get(v.id)}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      {versionAId === versionBId && (
        <Text size={200} className={s.err}>Pick two different versions to compare.</Text>
      )}

      <div className={s.configBlock}>
        <Field label="Dataset">
          <Dropdown
            placeholder={datasets.length === 0 ? 'No datasets for this role' : 'Select a dataset...'}
            value={datasets.find(d => d.id === datasetId)?.name ?? ''}
            selectedOptions={datasetId ? [datasetId] : []}
            onOptionSelect={(_e, d) => d.optionValue && setDatasetId(d.optionValue)}
            disabled={busy || datasets.length === 0}
          >
            {datasets.map(ds => (
              <Option key={ds.id} value={ds.id} text={ds.name}>
                {ds.name} · {ds.questionCount} Q
              </Option>
            ))}
          </Dropdown>
        </Field>

        <div>
          <Text weight="semibold" size={200}>Models</Text>
          <div className={s.modelGroupRow} style={{ marginTop: 6 }}>
            {Array.from(byProvider.entries()).map(([providerId, group]) => {
              const ids = group.map(m => m.chatModelId);
              const allOn = ids.every(id => selectedChatModelIds.has(id));
              const someOn = ids.some(id => selectedChatModelIds.has(id));
              return (
                <div key={providerId} className={s.modelGroup}>
                  <Checkbox
                    checked={allOn ? true : someOn ? 'mixed' : false}
                    onChange={() => toggleProviderGroup(providerId)}
                    disabled={busy}
                    label={
                      <Badge color={providerBadgeColor(providerId)} appearance="tint">
                        {providerLabel(providerId)}
                      </Badge>
                    }
                  />
                  {group.map(m => (
                    <Checkbox
                      key={m.chatModelId}
                      checked={selectedChatModelIds.has(m.chatModelId)}
                      onChange={() => toggleModel(m.chatModelId)}
                      disabled={busy}
                      label={<Text size={200}>{m.modelName}</Text>}
                    />
                  ))}
                </div>
              );
            })}
            {models.length === 0 && (
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                No configured chat models. Set provider keys in <code>.env</code>.
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- Preflight banner ---------------- */}
      {preflight && (
        <div className={`${s.preflightBanner} ${preflightWarn ? s.preflightWarn : ''}`}>
          <Text size={200} weight="semibold">Preflight:</Text>
          <Text size={200}>
            {preflight.versionA.cellCount} + {preflight.versionB.cellCount} cells ·
            est. ${preflight.totalEstimatedCostUsd.toFixed(4)} ·
            ~{preflight.totalEstimatedSeconds.toFixed(0)}s
          </Text>
          {preflightWarn && (
            <Badge color="warning" appearance="tint" size="small">
              Above threshold (&gt;$1 or &gt;5 min)
            </Badge>
          )}
        </div>
      )}
      {preflightErr && (
        <Text size={200} className={s.err}>Preflight: {preflightErr}</Text>
      )}

      {/* ---------------- Run button ---------------- */}
      <div className={s.runRow}>
        <Button appearance="primary" disabled={!canRun} onClick={handleRun}>
          {busy ? 'Running A/B...' : 'Run A/B test'}
        </Button>
        {busy && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Spinner size="tiny" />
            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>{loadingMsg}</Text>
          </div>
        )}
      </div>

      {/* ---------------- Results ---------------- */}
      {result && (
        <>
          <Divider />
          <div className={s.verdictCard}>
            <Badge color={verdictColor(result.verdict)} appearance="filled" size="large">
              {verdictLabel(result.verdict)}
            </Badge>
            <Text size={300}>
              v{result.versionANumber} <strong>vs</strong> v{result.versionBNumber} ·{' '}
              {result.perModel.length} model{result.perModel.length !== 1 ? 's' : ''} ·{' '}
              {result.datasetName}
            </Text>
            <div style={{ flex: 1 }} />
            <Button
              appearance="primary"
              disabled={!canPromote}
              onClick={() => setPromoteOpen(true)}
              title={promoteReason ?? `Promote ${winningLabel} to production`}
            >
              Promote winner
            </Button>
          </div>

          <div>
            <Text weight="semibold" size={300}>Per-model breakdown</Text>
            <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginTop: 2 }}>
              Click a row to see per-question detail side-by-side.
            </Text>
            <table className={s.table} style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th className={s.th}>Model</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>A avg</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>B avg</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Δ</th>
                  <th className={s.th} style={{ textAlign: 'right' }}>Samples (A / B)</th>
                  <th className={s.th}>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {result.perModel.map(row => (
                  <tr
                    key={row.chatModelId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openDrill(row)}
                  >
                    <td className={s.td}>
                      <Badge color={providerBadgeColor(row.chatModelId.split(':')[0])} appearance="tint">
                        {row.providerName} · {row.modelName}
                      </Badge>
                    </td>
                    <td className={s.td} style={{ textAlign: 'right' }}>
                      {pct(row.versionA.avgOverallScore)}
                    </td>
                    <td className={s.td} style={{ textAlign: 'right' }}>
                      {pct(row.versionB.avgOverallScore)}
                    </td>
                    <td className={s.td} style={{ textAlign: 'right' }}>
                      <Text
                        size={200}
                        style={{
                          color: row.deltaScore > 0
                            ? tokens.colorPaletteGreenForeground1
                            : row.deltaScore < 0
                              ? tokens.colorPaletteRedForeground1
                              : tokens.colorNeutralForeground2,
                        }}
                      >
                        {row.deltaScore >= 0 ? '+' : ''}{(row.deltaScore * 100).toFixed(1)}%
                      </Text>
                    </td>
                    <td className={s.td} style={{ textAlign: 'right' }}>
                      {row.versionA.sampleCount} / {row.versionB.sampleCount}
                      {(row.versionA.errorCount > 0 || row.versionB.errorCount > 0) && (
                        <Badge
                          size="small"
                          color="warning"
                          appearance="outline"
                          style={{ marginLeft: 6 }}
                        >
                          {row.versionA.errorCount + row.versionB.errorCount} err
                        </Badge>
                      )}
                    </td>
                    <td className={s.td}>
                      <Badge color={verdictColor(row.verdict)} appearance="tint">
                        {verdictLabel(row.verdict)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---------------- History ---------------- */}
      {history.length > 0 && (
        <>
          <Divider />
          <Text weight="semibold" size={300}>History</Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {history.map(h => (
              <div
                key={h.abTestId}
                className={`${s.historyRow} ${selectedHistoryId === h.abTestId ? s.historyRowActive : ''}`}
                onClick={() => loadHistoryItem(h.abTestId)}
              >
                <Text size={200} weight="semibold">
                  v{h.versionANumber} vs v{h.versionBNumber}
                </Text>
                <Badge color={verdictColor(h.verdict)} appearance="tint" size="small">
                  {verdictLabel(h.verdict)}
                </Badge>
                <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                  {h.chatModelCount} model{h.chatModelCount !== 1 ? 's' : ''} · {new Date(h.createdAt).toLocaleString()}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ---------------- Promote-winner confirm dialog ---------------- */}
      <Dialog open={promoteOpen} onOpenChange={(_e, d) => !d.open && setPromoteOpen(false)}>
        <DialogSurface style={{ maxWidth: 520 }}>
          <DialogBody>
            <DialogTitle>Promote winner to production?</DialogTitle>
            <DialogContent>
              {result && winningVersion && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Text size={300}>
                    This will advance <strong>{prompt.name}</strong>'s production version to{' '}
                    <strong>v{winningVersion.versionNumber}</strong> (winner: {winningLabel}).
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    An audit row will be written linking the promotion to this A/B test.
                    The prior production version will remain in the catalog.
                  </Text>
                  {promoteErr && (
                    <Text size={200} className={s.err}>{promoteErr}</Text>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                disabled={promoteBusy}
                onClick={() => setPromoteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={promoteBusy}
                onClick={handlePromote}
              >
                {promoteBusy ? 'Promoting...' : 'Promote'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ---------------- Drill dialog ---------------- */}
      <Dialog open={drill !== null} onOpenChange={(_e, d) => !d.open && setDrill(null)}>
        <DialogSurface style={{ maxWidth: 1100, width: '92vw' }}>
          <DialogBody>
            <DialogTitle>
              {drill && (
                <>
                  {drill.providerName} · {drill.modelName} — v{result?.versionANumber} vs v{result?.versionBNumber}
                </>
              )}
            </DialogTitle>
            <DialogContent>
              {drillLoading && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Spinner size="tiny" /> <Text size={200}>Loading matrix runs...</Text>
                </div>
              )}
              {!drillLoading && drillCells && drill && (
                <DrillPairs
                  a={drillCells.a}
                  b={drillCells.b}
                  aLabel={`v${result?.versionANumber}`}
                  bLabel={`v${result?.versionBNumber}`}
                  panelStyle={s.drillHalf}
                  answerStyle={s.drillAnswer}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDrill(null)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function defaultVersionA(p: PromptDetail): string {
  return p.productionVersionId ?? p.versions[0]?.id ?? '';
}

function defaultVersionB(p: PromptDetail): string {
  // Latest Draft, else highest version number that isn't A's default
  const a = defaultVersionA(p);
  const draft = [...p.versions].reverse().find(v => v.status === 'Draft' && v.id !== a);
  if (draft) return draft.id;
  const latest = [...p.versions].sort((x, y) => y.versionNumber - x.versionNumber).find(v => v.id !== a);
  return latest?.id ?? p.versions[p.versions.length - 1]?.id ?? '';
}

function filterAndSort(run: MatrixRunResponse, chatModelId: string): MatrixCellDto[] {
  return run.cells
    .filter(c => c.chatModelId === chatModelId)
    .sort((a, b) => a.questionOrderIndex - b.questionOrderIndex);
}

// Side-by-side per-question rendering, paired by questionOrderIndex.
function DrillPairs(props: {
  a: MatrixCellDto[];
  b: MatrixCellDto[];
  aLabel: string;
  bLabel: string;
  panelStyle: string;
  answerStyle: string;
}) {
  const { a, b, aLabel, bLabel, panelStyle, answerStyle } = props;
  const byIndex = new Map<number, { a?: MatrixCellDto; b?: MatrixCellDto }>();
  for (const c of a) byIndex.set(c.questionOrderIndex, { ...(byIndex.get(c.questionOrderIndex) ?? {}), a: c });
  for (const c of b) byIndex.set(c.questionOrderIndex, { ...(byIndex.get(c.questionOrderIndex) ?? {}), b: c });
  const ordered = Array.from(byIndex.keys()).sort((x, y) => x - y);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '60vh', overflow: 'auto' }}>
      {ordered.map(idx => {
        const pair = byIndex.get(idx)!;
        const q = pair.a?.questionText ?? pair.b?.questionText ?? '';
        const delta = (pair.b?.overallScore ?? 0) - (pair.a?.overallScore ?? 0);
        return (
          <div key={idx}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
              <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>Q{idx}</Text>
              <Text size={200} weight="semibold">{q}</Text>
              <Text
                size={200}
                style={{
                  marginLeft: 'auto',
                  color: delta > 0
                    ? tokens.colorPaletteGreenForeground1
                    : delta < 0
                      ? tokens.colorPaletteRedForeground1
                      : tokens.colorNeutralForeground2,
                }}
              >
                Δ {delta >= 0 ? '+' : ''}{(delta * 100).toFixed(1)}%
              </Text>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <CellPanel c={pair.a} label={aLabel} panelStyle={panelStyle} answerStyle={answerStyle} />
              <CellPanel c={pair.b} label={bLabel} panelStyle={panelStyle} answerStyle={answerStyle} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CellPanel(props: { c?: MatrixCellDto; label: string; panelStyle: string; answerStyle: string }) {
  const { c, label, panelStyle, answerStyle } = props;
  if (!c) {
    return (
      <div className={panelStyle}>
        <Text size={200} weight="semibold">{label}</Text>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No cell.</Text>
      </div>
    );
  }
  return (
    <div className={panelStyle}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <Text size={200} weight="semibold">{label}</Text>
        {c.isError ? (
          <Badge color="danger" appearance="tint" size="small">error</Badge>
        ) : (
          <Badge color="informative" appearance="tint" size="small">{pct(c.overallScore)}</Badge>
        )}
      </div>
      {c.isError ? (
        <Text size={200} className="err" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {c.error ?? 'Unknown error'}
        </Text>
      ) : (
        <div className={answerStyle}>{c.answer}</div>
      )}
    </div>
  );
}
